import { Loader2 } from "lucide-react"
import { useCallback, useEffect, useRef, useState } from "react"

interface SamOverlayProps {
  samMode: boolean
  samPending: boolean
  samError: string | null
  /** The container element over which the overlay is positioned (for pointer tracking) */
  containerRef: React.RefObject<HTMLDivElement | null>
}

interface DragBox {
  left: number
  top: number
  width: number
  height: number
}

export function SamOverlay({ samMode, samPending, samError, containerRef }: SamOverlayProps) {
  const [dragBox, setDragBox] = useState<DragBox | null>(null)
  const dragStartRef = useRef<{ x: number; y: number } | null>(null)

  const handlePointerDown = useCallback(
    (e: PointerEvent) => {
      if (e.button !== 0) return // left button only
      const container = containerRef.current
      if (!container) return
      const rect = container.getBoundingClientRect()
      dragStartRef.current = { x: e.clientX - rect.left, y: e.clientY - rect.top }
      setDragBox(null)
    },
    [containerRef]
  )

  const handlePointerMove = useCallback(
    (e: PointerEvent) => {
      const start = dragStartRef.current
      if (!start) return
      const container = containerRef.current
      if (!container) return
      const rect = container.getBoundingClientRect()
      const x = e.clientX - rect.left
      const y = e.clientY - rect.top

      setDragBox({
        left: Math.min(start.x, x),
        top: Math.min(start.y, y),
        width: Math.abs(x - start.x),
        height: Math.abs(y - start.y)
      })
    },
    [containerRef]
  )

  const handlePointerUp = useCallback(() => {
    dragStartRef.current = null
    setDragBox(null)
  }, [])

  useEffect(() => {
    if (!samMode || samPending) return
    const container = containerRef.current
    if (!container) return

    container.addEventListener("pointerdown", handlePointerDown)
    container.addEventListener("pointermove", handlePointerMove)
    container.addEventListener("pointerup", handlePointerUp)
    container.addEventListener("pointercancel", handlePointerUp)

    return () => {
      container.removeEventListener("pointerdown", handlePointerDown)
      container.removeEventListener("pointermove", handlePointerMove)
      container.removeEventListener("pointerup", handlePointerUp)
      container.removeEventListener("pointercancel", handlePointerUp)
      dragStartRef.current = null
      setDragBox(null)
    }
  }, [samMode, samPending, containerRef, handlePointerDown, handlePointerMove, handlePointerUp])

  if (!samMode) return null

  return (
    <div className="absolute inset-0 pointer-events-none z-10" style={{ cursor: "crosshair" }}>
      {dragBox && dragBox.width > 2 && dragBox.height > 2 && (
        <div
          className="absolute border-2 border-dashed border-primary/80 bg-primary/10 rounded-sm"
          style={{
            left: dragBox.left,
            top: dragBox.top,
            width: dragBox.width,
            height: dragBox.height
          }}
        />
      )}

      {samPending && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="flex items-center gap-2 rounded-md bg-card/90 border border-border px-3 py-2 shadow-md">
            <Loader2 className="size-4 animate-spin text-primary" />
            <span className="text-[11px] text-foreground">Segmenting...</span>
          </div>
        </div>
      )}

      {!(samPending || samError || dragBox) && (
        <div className="absolute bottom-3 left-1/2 -translate-x-1/2">
          <div className="rounded-md bg-card/80 border border-border px-2.5 py-1 shadow-sm">
            <span className="text-[10px] text-muted-foreground">Draw a box to segment with SAM</span>
          </div>
        </div>
      )}

      {samError && (
        <div className="absolute bottom-3 left-1/2 -translate-x-1/2">
          <div className="rounded-md bg-destructive/10 border border-destructive/30 px-2.5 py-1 shadow-sm">
            <span className="text-[10px] text-destructive">{samError}</span>
          </div>
        </div>
      )}
    </div>
  )
}
